import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Download, FileText, Maximize2, X } from "lucide-react";
import { resolveImg, refreshFileToken, downloadSigned } from "@/lib/api";
import { usePdfBlob } from "@/components/shared/usePdfBlob";

// 328: PDF jako malá dlaždice s první stranou, po kliknutí celá obrazovka (jako zvětšení fotky).
// Náhled je iframe s prohlížečovým čtečkou PDF; kde ho prohlížeč v rámečku neumí (část telefonů),
// zůstane ikona souboru a v okně je tlačítko Stáhnout.
function useSignedSrc(url) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let live = true;
    setSrc(null);
    Promise.race([refreshFileToken(), new Promise((r) => setTimeout(r, 8000))]).then(() => { if (live) setSrc(resolveImg(url)); });
    return () => { live = false; };
  }, [url]);
  return usePdfBlob(src);   // 336: blob: — přímou adresu Chrome v cizím rámečku blokuje
}

export function PdfViewer({ url, name, onClose }) {
  const src = useSignedSrc(url);
  useEffect(() => {
    // v zachytávací fázi na okně — dřív než Escape zavře panel pod náhledem
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);
  const btn = "h-11 px-3.5 rounded-full bg-white/15 hover:bg-white/25 text-white text-sm font-bold inline-flex items-center gap-1.5 shrink-0";
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={name || "PDF"} data-testid="pdf-viewer"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex flex-col pointer-events-auto animate-in fade-in duration-150 motion-reduce:animate-none">
      <div className="shrink-0 flex items-center gap-2 px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))]" onClick={(e) => e.stopPropagation()}>
        <span className="flex-1 min-w-0 truncate text-sm font-bold text-white">{name || "PDF"}</span>
        <button type="button" onClick={() => downloadSigned(url, name)} className={btn} data-testid="pdf-viewer-download" aria-label="Stáhnout">
          <Download size={16} /><span className="hidden sm:inline">Stáhnout</span>
        </button>
        <button type="button" onClick={onClose} className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white grid place-items-center shrink-0" aria-label="Zavřít" data-testid="pdf-viewer-close">
          <X size={22} />
        </button>
      </div>
      <div className="flex-1 min-h-0 md:px-6 pb-[max(0.5rem,env(safe-area-inset-bottom))] md:pb-6" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        {src && <iframe src={src} title={name || "PDF"} data-testid="pdf-viewer-frame" className="w-full h-full md:max-w-5xl md:mx-auto md:rounded-[12px] bg-white border-0" />}
      </div>
    </div>,
    document.body
  );
}

export function PdfTile({ url, name, testid }) {
  const [open, setOpen] = useState(false);
  const src = useSignedSrc(url);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} aria-label={`Zvětšit ${name || "PDF"}`} data-testid={testid || "pdf-tile"}
        className="group w-[136px] shrink-0 self-start text-left focus-visible:outline-none">
        <span className="relative block w-full h-[176px] rounded-[14px] border border-border bg-secondary overflow-hidden group-hover:border-foreground/30 group-focus-visible:ring-2 group-focus-visible:ring-brand transition-colors">
          <FileText size={34} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-muted-foreground/60" aria-hidden="true" />
          {src && (
            <iframe src={`${src}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`} title={name || "PDF"} tabIndex={-1} loading="lazy"
              className="absolute top-0 left-0 w-[272px] h-[352px] origin-top-left scale-50 pointer-events-none border-0 bg-white" />
          )}
          <span className="absolute right-1.5 bottom-1.5 w-7 h-7 rounded-full bg-card/95 border border-border grid place-items-center shadow-sm" aria-hidden="true">
            <Maximize2 size={14} className="text-muted-foreground group-hover:text-foreground" />
          </span>
        </span>
        <span className="mt-1 block text-[12.5px] font-semibold leading-snug break-words line-clamp-2">{name || "PDF"}</span>
      </button>
      {open && <PdfViewer url={url} name={name} onClose={() => setOpen(false)} />}
    </>
  );
}
